import React, { useState, useEffect, useContext, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import { WhatsApp, Forum, Close, Search, CheckCircle, ErrorOutline, PersonAdd } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { parseBRPhone, looksLikePhone } from "../../utils/parseBRPhone";

// 15/10/2026 (pedido do Patrick): só 2 abas - WhatsApp (número OU nome do
// contato no mesmo campo) e Chat Interno. Conexão sempre Escritório, sem
// perguntar. A verificação "tem WhatsApp?" via Baileys saiu: as conexões
// Consult estão desligadas (e Baileys bane número), então TODO número dava
// "não tem WhatsApp". A API Oficial não tem essa consulta - o número é
// reconhecido pelo formato e a Meta avisa na primeira entrega se não tiver.
const ESCRITORIO_FALLBACK_ID = 29;
const WA_GREEN = "#25d366";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const surface2 = dark ? "rgba(255,255,255,0.06)" : "#f3f5f4";
  const border = dark ? "rgba(255,255,255,0.12)" : "#dfe5e2";
  return {
    paper: { width: 420, maxWidth: "calc(100vw - 32px)", borderRadius: 16, overflow: "hidden" },
    header: { display: "flex", alignItems: "center", padding: "16px 12px 8px 20px" },
    title: { flex: 1, fontSize: 18, fontWeight: 700 },
    body: { padding: "8px 20px 4px" },
    seg: {
      display: "flex", padding: 4, borderRadius: 12, background: surface2, gap: 4, marginBottom: 16,
    },
    segBtn: {
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      border: "none", borderRadius: 9, padding: "9px 8px", cursor: "pointer",
      background: "transparent", color: theme.palette.text.secondary,
      fontSize: 14, fontWeight: 600, fontFamily: "inherit", transition: "background .15s, color .15s",
      "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
    },
    segActive: {
      background: theme.palette.background.paper, color: theme.palette.text.primary,
      boxShadow: dark ? "0 1px 3px rgba(0,0,0,.5)" : "0 1px 3px rgba(0,0,0,.12)",
    },
    field: {
      display: "flex", alignItems: "center", gap: 8, border: `1.5px solid ${border}`,
      borderRadius: 12, padding: "4px 12px", transition: "border-color .15s",
      "&:focus-within": { borderColor: theme.palette.primary.main },
    },
    fieldOk: { borderColor: WA_GREEN, "&:focus-within": { borderColor: WA_GREEN } },
    fieldErr: { borderColor: "#e5484d", "&:focus-within": { borderColor: "#e5484d" } },
    input: { flex: 1, fontSize: 16, padding: "8px 0" },
    hint: { fontSize: 12.5, color: theme.palette.text.secondary, marginTop: 8, lineHeight: 1.4 },
    status: {
      display: "flex", alignItems: "center", gap: 12, marginTop: 12, padding: "12px 14px",
      borderRadius: 12, background: surface2,
    },
    statusOk: { background: dark ? "rgba(37,211,102,0.14)" : "rgba(37,211,102,0.10)" },
    statusErr: { background: dark ? "rgba(229,72,77,0.16)" : "rgba(229,72,77,0.09)", color: "#e5484d" },
    statusMain: { fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums" },
    statusSub: { fontSize: 12.5, color: theme.palette.text.secondary, marginTop: 2 },
    avatar: {
      width: 38, height: 38, borderRadius: "50%", flexShrink: 0, display: "flex",
      alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15,
      background: dark ? "rgba(255,255,255,0.12)" : "#e3e8e6", color: theme.palette.text.primary,
      overflow: "hidden",
      "& img": { width: "100%", height: "100%", objectFit: "cover" },
    },
    list: { marginTop: 10, maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 },
    row: {
      display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", borderRadius: 10,
      cursor: "pointer", border: "none", background: "transparent", textAlign: "left",
      fontFamily: "inherit", color: "inherit", width: "100%",
      "&:hover": { background: surface2 },
    },
    rowSel: { background: dark ? "rgba(37,211,102,0.14)" : "rgba(37,211,102,0.10)" },
    rowName: { fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    rowNum: { fontSize: 12.5, color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums" },
    nameField: { marginTop: 10 },
    via: {
      display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
      color: theme.palette.text.secondary, marginTop: 14,
    },
    dot: { width: 7, height: 7, borderRadius: "50%", background: WA_GREEN },
    actions: { display: "flex", gap: 10, padding: "16px 20px 20px" },
    cancel: { flex: 1, textTransform: "none", borderRadius: 10, fontWeight: 600 },
    start: {
      flex: 2, textTransform: "none", borderRadius: 10, fontWeight: 700, color: "#fff",
      background: WA_GREEN, boxShadow: "none",
      "&:hover": { background: "#1fb457", boxShadow: "none" },
      "&.Mui-disabled": { background: dark ? "rgba(255,255,255,0.12)" : "#d7dcda", color: dark ? "rgba(255,255,255,0.4)" : "#8a938f" },
    },
    startInternal: { background: theme.palette.primary.main, "&:hover": { background: theme.palette.primary.dark } },
  };
});

const initialsOf = (name) => (String(name || "?").trim()[0] || "?").toUpperCase();
const formatStoredNumber = (n) => {
  const p = parseBRPhone(n);
  return p.ok ? p.display : n;
};

const NewTicketModal = ({ modalOpen, onClose, initialContact, initialSearchParam }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { companyId } = user;
  const inputRef = useRef(null);

  const [mode, setMode] = useState("whatsapp");
  const [loading, setLoading] = useState(false);
  const [escritorioId, setEscritorioId] = useState(ESCRITORIO_FALLBACK_ID);

  // aba WhatsApp: um campo só - número ou nome
  const [query, setQuery] = useState("");
  const [newName, setNewName] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [numberMatch, setNumberMatch] = useState(null);
  // null | "checking" | { checked, exists } - consulta pelo chip "Verificador WhatsApp"
  const [waCheck, setWaCheck] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  // aba Chat Interno (conexão "Local"): ID de 11 dígitos
  const [internalId, setInternalId] = useState("");
  const [internalLookupLoading, setInternalLookupLoading] = useState(false);
  const [internalLookupResult, setInternalLookupResult] = useState(null);
  const [internalError, setInternalError] = useState("");

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  const isPhone = looksLikePhone(query);
  const parsed = isPhone ? parseBRPhone(query) : null;

  useEffect(() => {
    if (!modalOpen) return;
    api.get(`/whatsapp`, { params: { companyId, session: 0 } })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        const esc = list.find(w => /escrit/i.test(w.name || "") && w.channel === "whatsapp_oficial")
          || list.find(w => w.id === ESCRITORIO_FALLBACK_ID);
        if (esc) setEscritorioId(esc.id);
      })
      .catch(() => {});
  }, [modalOpen, companyId]);

  useEffect(() => {
    if (initialContact?.id !== undefined) setSelectedContact(initialContact);
  }, [initialContact]);

  useEffect(() => {
    if (modalOpen && initialSearchParam) setQuery(String(initialSearchParam));
  }, [modalOpen, initialSearchParam]);

  useEffect(() => {
    if (modalOpen) setTimeout(() => inputRef.current && inputRef.current.focus(), 150);
  }, [modalOpen, mode]);

  // Busca enquanto digita: nome -> lista de contatos; número válido -> acha o
  // contato já salvo (com ou sem o 9) pra reaproveitar a conversa.
  useEffect(() => {
    if (!modalOpen || mode !== "whatsapp" || initialContact?.id !== undefined) return;
    const q = query.trim();
    setNumberMatch(null);
    setWaCheck(null);
    if (isPhone) {
      setResults([]);
      if (!parsed?.ok) { setSearching(false); return; }
    } else if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const searchParam = isPhone ? parsed.number.slice(-8) : q;
        const { data } = await api.get("contacts", { params: { searchParam } });
        const contacts = (data?.contacts || []).filter(c => !c.isGroup && c.channel !== "internal");
        if (isPhone) {
          const hit = contacts.find(c => parsed.variants.includes(String(c.number || "").replace(/\D/g, "")));
          setNumberMatch(hit || null);
          if (!hit) {
            setWaCheck("checking");
            try {
              const { data: chk } = await api.post("/contacts/check-whatsapp", {
                number: parsed.number,
                forceBaileysConnection: true,
              });
              setWaCheck({ checked: !!chk?.checked, exists: !!chk?.exists });
            } catch (_) {
              setWaCheck({ checked: false, exists: false });
            }
          }
        } else {
          setResults(contacts.slice(0, 8));
        }
      } catch (err) {
        toastError(err);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, modalOpen, mode]);

  // Chat Interno: busca sozinho quando completa os 11 dígitos
  useEffect(() => {
    if (!modalOpen || mode !== "internal") return;
    const id = internalId.replace(/\D/g, "");
    setInternalLookupResult(null);
    setInternalError("");
    if (id.length !== 11) return;
    setInternalLookupLoading(true);
    let alive = true;
    api.get(`/internal-chat-users/lookup/${id}`)
      .then(({ data }) => { if (alive) setInternalLookupResult(data); })
      .catch(err => { if (alive) setInternalError(err.response?.data?.error || "Nenhum usuário com esse ID."); })
      .finally(() => { if (alive) setInternalLookupLoading(false); });
    return () => { alive = false; };
  }, [internalId, modalOpen, mode]);

  const resetAll = () => {
    setQuery("");
    setNewName("");
    setResults([]);
    setNumberMatch(null);
    setSelectedContact(null);
    setInternalId("");
    setInternalLookupResult(null);
    setInternalError("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setMode("whatsapp");
    setLoading(false);
  };

  const handleClose = () => {
    onClose();
    resetAll();
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSaveTicket = async contactId => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        queueId: null,
        whatsappId: escritorioId,
        userId: user.id,
        status: "open",
      });
      onClose(ticket);
      resetAll();
    } catch (err) {
      setLoading(false);
      // 15/10/2026: conversa já existe -> abre ela direto em vez do aviso
      // "Atendimento Existente". Se estiver esperando (sem atendente), já
      // assume pra mim; se for de outro atendente, só admin abre direto.
      const existing = err.response?.status === 403
        ? err.response.data?.ticket
        : err.response?.status === 409 ? (() => { try { return JSON.parse(err.response.data.error); } catch (_) { return null; } })() : null;
      if (existing?.uuid && (!existing.userId || existing.userId === user?.id || user?.profile === "admin")) {
        try {
          if (!existing.userId && existing.status !== "open") {
            await api.put(`/tickets/${existing.id}`, { status: "open", userId: user.id, queueId: existing.queueId || null });
          }
        } catch (_) { /* abre mesmo assim */ }
        onClose(existing);
        resetAll();
        return;
      }
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        setOpenAlert(true);
        setUserTicketOpen(errorData.ticket?.user?.name || "Outro usuário");
        setQueueTicketOpen(errorData.ticket?.queue?.name || "Sem fila");
        return;
      }
      if (err.response?.status === 409) {
        const ticket = JSON.parse(err.response.data.error);
        if (ticket.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(ticket?.user?.name);
          setQueueTicketOpen(ticket?.queue?.name);
        } else {
          onClose(ticket);
          resetAll();
        }
        return;
      }
      toastError(err);
    }
  };

  const handleStartWhatsapp = async () => {
    if (selectedContact) return handleSaveTicket(selectedContact.id);
    if (!parsed?.ok) return;
    if (numberMatch) return handleSaveTicket(numberMatch.id);
    if (!waCheck || waCheck === "checking" || (waCheck.checked && !waCheck.exists)) return;
    setLoading(true);
    try {
      const { data: contact } = await api.post("/contacts/find-or-create-by-number", {
        number: parsed.number,
        name: newName.trim() || undefined,
      });
      await handleSaveTicket(contact.id);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleStartInternal = async () => {
    if (!internalLookupResult) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets/internal/start-conversation", {
        targetInternalChatId: internalId.replace(/\D/g, ""),
      });
      onClose(ticket);
      resetAll();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const waBlocked = !waCheck || waCheck === "checking" || (waCheck.checked && !waCheck.exists);
  const canStartWa = !!selectedContact || (!!parsed?.ok && (!!numberMatch || (!searching && !waBlocked)));
  const canStart = mode === "whatsapp" ? canStartWa : !!internalLookupResult;
  const onStart = mode === "whatsapp" ? handleStartWhatsapp : handleStartInternal;
  const fixedContact = initialContact?.id !== undefined;

  const renderContactCard = (c, sub) => (
    <div className={`${classes.status} ${classes.statusOk}`}>
      <div className={classes.avatar}>
        {c.urlPicture || c.profilePicUrl ? <img src={c.urlPicture || c.profilePicUrl} alt="" /> : initialsOf(c.name)}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className={classes.statusMain} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
        <div className={classes.statusSub}>{sub}</div>
      </div>
      {!fixedContact && (
        <IconButton size="small" onClick={() => setSelectedContact(null)} aria-label="Trocar contato">
          <Close fontSize="small" />
        </IconButton>
      )}
    </div>
  );

  const renderWhatsapp = () => {
    if (selectedContact) {
      return renderContactCard(selectedContact, formatStoredNumber(selectedContact.number));
    }
    const fieldState = parsed ? (parsed.ok ? classes.fieldOk : (parsed.empty || parsed.incomplete ? "" : classes.fieldErr)) : "";
    return (
      <>
        <div className={`${classes.field} ${fieldState}`}>
          {isPhone ? <WhatsApp style={{ color: parsed?.ok ? WA_GREEN : undefined, opacity: parsed?.ok ? 1 : 0.5 }} /> : <Search style={{ opacity: 0.5 }} />}
          <InputBase
            id="newticket-query"
            inputRef={inputRef}
            className={classes.input}
            placeholder="Número com DDD ou nome do contato"
            value={query}
            autoComplete="off"
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && canStartWa && !loading) handleStartWhatsapp(); }}
          />
          {searching && <CircularProgress size={18} />}
          {!!query && !searching && (
            <IconButton size="small" onClick={() => { setQuery(""); inputRef.current && inputRef.current.focus(); }} aria-label="Limpar">
              <Close fontSize="small" />
            </IconButton>
          )}
        </div>

        {!query && (
          <div className={classes.hint}>
            Cole ou digite do jeito que tiver: +55 11 91234-5678, (17) 99148-2261, 5517991482261…
          </div>
        )}

        {isPhone && parsed?.incomplete && <div className={classes.hint}>{parsed.error}</div>}

        {isPhone && parsed && !parsed.ok && !parsed.empty && !parsed.incomplete && (
          <div className={`${classes.status} ${classes.statusErr}`}>
            <ErrorOutline />
            <div style={{ fontSize: 14, fontWeight: 600 }}>{parsed.error}</div>
          </div>
        )}

        {isPhone && parsed?.ok && numberMatch && renderContactCard(numberMatch, `${parsed.display} · já está nos contatos`)}

        {isPhone && parsed?.ok && !numberMatch && (
          <>
            {(() => {
              const noWa = waCheck && waCheck !== "checking" && waCheck.checked && !waCheck.exists;
              const unchecked = waCheck && waCheck !== "checking" && !waCheck.checked;
              const busy = searching || waCheck === "checking";
              const cls = noWa ? classes.statusErr : (busy || unchecked) ? "" : classes.statusOk;
              const sub = searching ? "Procurando nos contatos…"
                : waCheck === "checking" ? "Conferindo se tem WhatsApp…"
                : noWa ? "Esse número não tem WhatsApp"
                : unchecked ? "Não deu pra conferir agora (verificador desligado) · pode tentar mesmo assim"
                : `Tem WhatsApp · ${parsed.landline ? "número fixo, " : ""}contato novo${parsed.addedNine ? " (coloquei o 9)" : ""}`;
              return (
                <div className={`${classes.status} ${cls}`}>
                  {busy ? <CircularProgress size={22} />
                    : noWa ? <ErrorOutline />
                    : unchecked ? <ErrorOutline style={{ color: "#e5a000" }} />
                    : <CheckCircle style={{ color: WA_GREEN }} />}
                  <div>
                    <div className={classes.statusMain} style={noWa ? { color: "inherit" } : undefined}>{parsed.display}</div>
                    <div className={classes.statusSub} style={noWa ? { color: "inherit", fontWeight: 600 } : undefined}>{sub}</div>
                  </div>
                </div>
              );
            })()}
            {!searching && !waBlocked && (
              <div className={`${classes.field} ${classes.nameField}`}>
                <PersonAdd style={{ opacity: 0.5 }} />
                <InputBase
                  id="newticket-name"
                  className={classes.input}
                  placeholder="Nome do contato (opcional)"
                  value={newName}
                  onChange={e => setNewName(e.target.value.replace(/(^|\s)(\S)/g, (m, s, ch) => s + ch.toUpperCase()))}
                  onKeyDown={e => { if (e.key === "Enter" && !loading) handleStartWhatsapp(); }}
                />
              </div>
            )}
          </>
        )}

        {!isPhone && results.length > 0 && (
          <div className={classes.list}>
            {results.map(c => (
              <button type="button" key={c.id} className={classes.row} onClick={() => setSelectedContact(c)}>
                <div className={classes.avatar}>
                  {c.urlPicture || c.profilePicUrl ? <img src={c.urlPicture || c.profilePicUrl} alt="" /> : initialsOf(c.name)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className={classes.rowName}>{c.name}</div>
                  <div className={classes.rowNum}>{formatStoredNumber(c.number)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
        {!isPhone && query.trim().length >= 2 && !searching && results.length === 0 && (
          <div className={classes.hint}>Nenhum contato com esse nome. Digite o número pra começar uma conversa nova.</div>
        )}
      </>
    );
  };

  const renderInternal = () => (
    <>
      <div className={`${classes.field} ${internalLookupResult ? classes.fieldOk : internalError ? classes.fieldErr : ""}`}>
        <Forum style={{ opacity: 0.5 }} />
        <InputBase
          id="newticket-internal-id"
          inputRef={inputRef}
          className={classes.input}
          placeholder="ID de 11 dígitos"
          value={internalId}
          inputProps={{ inputMode: "numeric", maxLength: 14 }}
          onChange={e => setInternalId(e.target.value.replace(/\D/g, "").slice(0, 11))}
          onKeyDown={e => { if (e.key === "Enter" && internalLookupResult && !loading) handleStartInternal(); }}
        />
        {internalLookupLoading && <CircularProgress size={18} />}
      </div>
      {!internalLookupResult && !internalError && (
        <div className={classes.hint}>A pessoa aparece sozinha quando os 11 dígitos estiverem completos.</div>
      )}
      {internalError && (
        <div className={`${classes.status} ${classes.statusErr}`}>
          <ErrorOutline />
          <div style={{ fontSize: 14, fontWeight: 600 }}>{internalError}</div>
        </div>
      )}
      {internalLookupResult && (
        <div className={`${classes.status} ${classes.statusOk}`}>
          <div className={classes.avatar}>{initialsOf(internalLookupResult.name)}</div>
          <div className={classes.statusMain}>{internalLookupResult.name}</div>
        </div>
      )}
    </>
  );

  return (
    <>
      <Dialog open={modalOpen} onClose={handleClose} classes={{ paper: classes.paper }}>
        <div className={classes.header}>
          <Typography className={classes.title}>Nova conversa</Typography>
          <IconButton size="small" onClick={handleClose} aria-label="Fechar"><Close /></IconButton>
        </div>
        <div className={classes.body}>
          {!fixedContact && (
            <div className={classes.seg} role="tablist">
              <button
                type="button" role="tab" aria-selected={mode === "whatsapp"}
                className={`${classes.segBtn} ${mode === "whatsapp" ? classes.segActive : ""}`}
                onClick={() => setMode("whatsapp")}
              >
                <WhatsApp style={{ fontSize: 18, color: mode === "whatsapp" ? WA_GREEN : undefined }} /> WhatsApp
              </button>
              <button
                type="button" role="tab" aria-selected={mode === "internal"}
                className={`${classes.segBtn} ${mode === "internal" ? classes.segActive : ""}`}
                onClick={() => setMode("internal")}
              >
                <Forum style={{ fontSize: 18 }} /> Chat Interno
              </button>
            </div>
          )}
          {mode === "whatsapp" ? renderWhatsapp() : renderInternal()}
          {mode === "whatsapp" && (
            <div className={classes.via}><span className={classes.dot} /> Sai pelo WhatsApp do Escritório</div>
          )}
        </div>
        <div className={classes.actions}>
          <Button variant="outlined" className={classes.cancel} onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            className={`${classes.start} ${mode === "internal" ? classes.startInternal : ""}`}
            disabled={!canStart || loading}
            onClick={onStart}
          >
            {loading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Iniciar conversa"}
          </Button>
        </div>
        {openAlert && (
          <ShowTicketOpen
            isOpen={openAlert}
            handleClose={handleCloseAlert}
            user={userTicketOpen}
            queue={queueTicketOpen}
          />
        )}
      </Dialog>
    </>
  );
};

export default NewTicketModal;
